import { LogLevel } from '@signage-player/contracts';
import { AxiosWrapper, sanitizeForLogging, isNetworkError, monotonicMs } from '@signage-player/shared-logic';
import { AuthInstance, CacheKey } from '@signage-player/data-store';
import { CacheStoreTag } from '@signage-player/cache';
/**
 * Authentication service for main process
 * Handles device authentication and server pairing
 */
export class AuthService {
    constructor(config, logger, deviceService, cacheManager, broadcast) {
        this.pollingInterval = null;
        this.pairingPollStartedAt = 0;
        this.config = config;
        this.logger = logger;
        this.deviceService = deviceService;
        this.cacheManager = cacheManager;
        this.broadcast = broadcast;
        this.authAxios = new AxiosWrapper(this.config.authUrl, logger, undefined, {
            logRequestContext: config.logging.logRequestContext,
            logResponseContext: config.logging.logResponseContext,
        });
        this.authInstance = this.login();
    }
    /**
     * Perform device authentication
     */
    async login(isPollingRequest = false) {
        try {
            if (!isPollingRequest)
                this.logger.debug('[AuthService] Starting device authentication...');
            const deviceInfo = await this.deviceService.deviceInfo;
            if (!deviceInfo.hash) {
                this.logger.error('[AuthService] Device hash is missing, cannot authenticate');
                throw new Error('Device hash is missing, cannot authenticate');
            }
            let deviceByHash = await this.getDeviceByHashOrConnectString(deviceInfo, isPollingRequest);
            if (deviceByHash === false) {
                this.logger.debug('[AuthService] Device not found on server, will register the device first');
                await this.registerDevice(deviceInfo);
                deviceByHash = await this.getDeviceByHashOrConnectString(deviceInfo, isPollingRequest);
            }
            if (!deviceByHash) {
                return null;
            }
            const isPaired = !!deviceByHash?.server?.id;
            const email = deviceByHash.connect_string.replaceAll(' ', '') + '@devices.bsgn.cc';
            let token = null;
            if (isPaired) {
                token = await this.loginOnTenantServer(deviceByHash.server.url, email, deviceByHash.hash, isPollingRequest);
            }
            this.logger.info('[AuthService] Authentication request completed', sanitizeForLogging({
                isPaired,
                connectString: deviceByHash.connect_string,
                serverDomain: deviceByHash.server?.domain,
            }));
            return new AuthInstance(email, deviceByHash.hash, deviceByHash.server?.url, deviceByHash.hash, deviceByHash.server?.tenant_id || null, deviceByHash.connect_string, isPaired, token);
        }
        catch (error) {
            this.logger.error('[AuthService] Authentication failed:', { error: error });
            return null;
        }
    }
    /**
     * Get current authentication instance
     */
    getAuthInstance() {
        return this.authInstance;
    }
    /**
     * Check if device is authenticated
     */
    isAuthenticated() {
        return this.authInstance.then(instance => !!instance && !!instance.token);
    }
    /**
     * Check if device is paired with a server
     */
    isPaired() {
        return this.authInstance.then(instance => (instance ? instance.isPaired : false));
    }
    activateAuthInstancePolling(callback, initialIntervalMs = 5000) {
        if (this.pollingInterval) {
            clearTimeout(this.pollingInterval);
        }
        this.pairingPollStartedAt = 0;
        let isFirstPoll = true;
        const poll = async () => {
            // Anchor backoff window from first actual poll, not from activation —
            // otherwise the first interval is counted against elapsed time.
            if (isFirstPoll) {
                this.pairingPollStartedAt = monotonicMs();
                isFirstPoll = false;
            }
            try {
                const oldAuthInstace = await this.authInstance;
                this.authInstance = this.login(true);
                this.authInstance
                    .then(newAuthInstance => {
                    try {
                        callback(newAuthInstance, oldAuthInstace);
                    }
                    catch (err) {
                        this.logger.error('[AuthService] Polling callback threw.', { error: err });
                    }
                })
                    .catch(err => {
                    // login() catches its own errors and resolves to null, so this should
                    // never fire. Defending against future contract changes.
                    this.logger.error('[AuthService] Polling login() promise rejected unexpectedly.', { error: err });
                });
            }
            catch (err) {
                // Keep the polling loop alive on unexpected throw — otherwise an
                // unrelated change in login() or authInstance state could brick it forever.
                this.logger.error('[AuthService] Polling iteration threw, will retry.', { error: err });
            }
            // Exponential backoff: 5s for first hour, 10s for second hour, 20s for third, then 30min.
            // Use monotonic time so an NTP step on Pi boot can't jump elapsed negative or huge.
            const elapsed = monotonicMs() - this.pairingPollStartedAt;
            let nextInterval;
            if (elapsed < 60 * 60 * 1000) {
                // first hour
                nextInterval = initialIntervalMs; // 5s
            }
            else if (elapsed < 2 * 60 * 60 * 1000) {
                // second hour
                nextInterval = 10_000; // 10s
            }
            else if (elapsed < 3 * 60 * 60 * 1000) {
                // third hour
                nextInterval = 20_000; // 20s
            }
            else {
                // after 3 hours
                nextInterval = 30 * 60 * 1000; // 30 minutes
            }
            this.pollingInterval = setTimeout(poll, nextInterval);
        };
        this.pollingInterval = setTimeout(poll, initialIntervalMs);
    }
    stopAuthInstancePolling() {
        if (this.pollingInterval) {
            clearTimeout(this.pollingInterval);
            this.pollingInterval = null;
        }
    }
    /**
     * @returns {boolean} Returns true if auth instance changed and device is paired and logged into server, false otherwise
     */
    checkIfAuthInstanceChanged(oldAuthInstace, newAuthInstance) {
        if (!newAuthInstance?.isSameAs(oldAuthInstace) && newAuthInstance !== oldAuthInstace) {
            this.logger.debug('[Main] Auth instance updated:', sanitizeForLogging({
                newAuthInstance: newAuthInstance?.toLogObject() || null,
                oldAuthInstace: oldAuthInstace?.toLogObject() || null,
            }));
            this.broadcastAuthInstanceUpdate(newAuthInstance);
            if (newAuthInstance?.isPaired && newAuthInstance.token) {
                this.stopAuthInstancePolling();
                return true;
            }
        }
        return false;
    }
    broadcastAuthInstanceUpdate(newAuthInstance) {
        // Notify renderer processes about auth instance update
        this.broadcast.broadcast('auth-instance-update', newAuthInstance);
    }
    /**
     * Get device information from server by hash or connect string
     */
    async getDeviceByHashOrConnectString(deviceInfo, isPollingRequest = false) {
        try {
            this.logger.debug('[AuthService] Fetching device info from server...', {
                hash: deviceInfo.hash,
            });
            let apiRes;
            if (isPollingRequest) {
                const axios = new AxiosWrapper(this.authAxios.baseURL);
                apiRes = await axios.get(`/api/v1/devices/${encodeURIComponent(deviceInfo.hash || '')}`, { validateStatus: false });
            }
            else {
                apiRes = await this.authAxios.get(`/api/v1/devices/${encodeURIComponent(deviceInfo.hash || '')}`, { validateStatus: false });
            }
            if (apiRes.status < 200 || apiRes.status > 305) {
                this.logger.warning('[AuthService] Server returned non-success status', {
                    status: apiRes.status,
                    statusText: apiRes.statusText,
                });
                if (apiRes.status === 404) {
                    return false; // Device not found
                }
                return null;
            }
            // Cache the result
            await this.cacheAuthResult(apiRes.data, isPollingRequest);
            return apiRes.data;
        }
        catch (err) {
            this.logger.warning('[AuthService] Auth server not available, trying cached result...', { error: err });
            const cached = await this.loadCachedAuthResult();
            // Cache fallback only makes sense for already-paired devices
            if (cached && !cached.server?.id) {
                this.logger.notice('[AuthService] Discarding cached unpaired device record - surfacing network failure to renderer');
                return null;
            }
            return cached;
        }
    }
    async registerDevice(deviceInfo) {
        try {
            this.logger.debug('[AuthService] Registering device on server...', {
                hash: deviceInfo.hash,
            });
            const apiRes = await this.authAxios.post(`/api/v1/devices/`, {
                manufacturer: deviceInfo.manufacturer,
                mac: deviceInfo.mac,
                hash: deviceInfo.hash,
                mbid: deviceInfo.mbid,
                cpu_name: deviceInfo.cpuName,
                product_name: deviceInfo.productName,
                ram: deviceInfo.ram,
                arch: deviceInfo.arch,
            });
            this.logger.debug('[AuthService] Device registered successfully', sanitizeForLogging({ response: apiRes.data }));
            return apiRes.data;
        }
        catch (err) {
            this.logger.error('[AuthService] Device registration failed:', { error: err });
            return undefined;
        }
    }
    /**
     * Pre-pairing screen topology report (`PUT /api/v1/devices/{hash}` on the auth server), so the tenant
     * can copy screen_list/platform on devices/connect. Best effort: true only on a 2xx; 404 means the
     * device row isn't registered yet and the caller retries on its next trigger. Never throws.
     */
    async reportDeviceScreens(hash, payload) {
        try {
            const apiRes = await this.authAxios.put(`/api/v1/devices/${encodeURIComponent(hash)}`, payload, { validateStatus: false });
            if (apiRes.status >= 200 && apiRes.status < 300) {
                return true;
            }
            this.logger.log(apiRes.status === 404 ? LogLevel.DEBUG : LogLevel.WARNING, '[AuthService] Screen report rejected by auth server', {
                status: apiRes.status,
                statusText: apiRes.statusText,
            });
            return false;
        }
        catch (err) {
            this.logger.log(isNetworkError(err) ? LogLevel.NOTICE : LogLevel.WARNING, '[AuthService] Screen report failed', { error: err });
            return false;
        }
    }
    /**
     * @returns {Promise<string|null>} Returns token string if login successful, null otherwise
     */
    async loginOnTenantServer(serverUrl, email, password, isPollingRequest = false) {
        try {
            if (!isPollingRequest)
                this.logger.debug('[AuthService] Logging in on tenant server...', { serverUrl, email });
            const axiosWrapper = new AxiosWrapper(serverUrl, isPollingRequest ? undefined : this.logger, undefined, {
                logRequestContext: this.config.logging.logRequestContext,
                logResponseContext: this.config.logging.logResponseContext,
            });
            const apiRes = await axiosWrapper.post('/api/v1/device-login', {
                identifier: email,
                password,
            });
            if (apiRes.status === 200 && apiRes.data.jwt) {
                this.logger.debug('[AuthService] Logged in on tenant server successfully');
                this.logger.debug('[AuthService] Received JWT token');
                this.cacheManager.tag(CacheStoreTag.Persistent).set(CacheKey.AuthTenantServer, apiRes.data, 60 * 60 * 24 * 7);
                return apiRes.data.jwt;
            }
            else {
                this.logger.warning('[AuthService] Failed to log in on tenant server', { status: apiRes.status, statusText: apiRes.statusText, data: apiRes.data });
                return this.loadCachedAuthTenantServerResult();
            }
        }
        catch (err) {
            this.logger.log(isNetworkError(err) ? LogLevel.NOTICE : LogLevel.ERROR, '[AuthService] Error logging in on tenant server:', { error: err });
            return this.loadCachedAuthTenantServerResult();
        }
    }
    async cacheAuthResult(data, isPollingRequest = false) {
        this.cacheManager.tag(CacheStoreTag.Persistent).set(CacheKey.Auth, data, 60 * 60 * 24 * 7); // Cache for 1 week
        if (!isPollingRequest)
            this.logger.debug('[AuthService] Authentication result cached successfully');
    }
    async loadCachedAuthResult() {
        const result = await this.cacheManager.tag(CacheStoreTag.Persistent).get(CacheKey.Auth);
        if (!result) {
            this.logger.info('[AuthService] No cached authentication result found');
            return null;
        }
        this.logger.info('[AuthService] Using cached authentication result');
        return result;
    }
    async loadCachedAuthTenantServerResult() {
        const result = await this.cacheManager.tag(CacheStoreTag.Persistent).get(CacheKey.AuthTenantServer);
        if (!result) {
            this.logger.info('[AuthService] No cached authentication result found for tenant server');
            return null;
        }
        if (typeof result?.jwt !== 'string' || result.jwt.length === 0) {
            this.logger.warning('[AuthService] Cached tenant server authentication result has invalid or missing JWT, discarding');
            return null;
        }
        this.logger.info('[AuthService] Using cached authentication result of tenatn server');
        return result.jwt;
    }
}
export default AuthService;
//# sourceMappingURL=AuthService.js.map